import { useCallback, useMemo } from 'react';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import useFoodware from 'hooks/use-foodware';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useOrderContext } from 'state/order';
import { IPendingReorder } from 'state/order/types';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';

import { isOfferType } from '../utils';

export const useReorder = (order?: IPendingReorder | null) => {
  const {
    reorder: { handleReorder, setPendingReorder },
  } = useOrderContext();
  const { isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
  const { setStoreLocatorCallbackUrl } = useLocationContext();
  const { navigate } = useNavigation();
  const { pathname } = useRoute();
  const enableReorder = useFlag(LaunchDarklyFlag.ENABLE_REORDER);
  const { isFoodware } = useFoodware();

  const hasOffer = useMemo(() => {
    return order && order.cart
      ? (order.cart.cartEntries || []).some((entry: IBackendCartEntries) => isOfferType(entry.type))
      : false;
  }, [order]);

  const handleReorderClick = useCallback(() => {
    if (!order) {
      return;
    }

    const filteredReorderCartEntries = order.cart.reorderCartEntries?.filter(
      item => !(item.isDonation || isFoodware(item))
    );

    const filteredOrder: IPendingReorder = {
      ...order,
      cart: {
        ...order.cart,
        reorderCartEntries: filteredReorderCartEntries,
      },
    };

    if (isStoreOpenAndAvailable) {
      handleReorder(filteredOrder);
    } else {
      setPendingReorder(filteredOrder);
      setStoreLocatorCallbackUrl(pathname);
      navigate(routes.storeLocator, { replace: true });
    }
  }, [
    order,
    isStoreOpenAndAvailable,
    isFoodware,
    handleReorder,
    setPendingReorder,
    setStoreLocatorCallbackUrl,
    pathname,
    navigate,
  ]);

  const disabled = !enableReorder || hasOffer || (!isStoreOpenAndAvailable && !noStoreSelected);

  return { handleReorderClick, disabled };
};
